// ADOPS-2706 — helpers for carrying (or dropping) the Receipts KPI when a line item is
// duplicated. The Receipts goal itself lives in the line item's kpi_goal column, so the
// backend's attribute-level dup already copies it; its product/category selections live
// in the separate receipt_product_list association, which the backend only clones when
// asked. These helpers let the duplicate modal detect a Receipts KPI on the source and,
// when the user deselects it, build the kpi_goal override that drops it.
import { RECEIPTS_KPI_NAME } from '../constants/lineItems';

const toLabels = (items: any[] = []) => items.map((item) => (item as any)?.label ?? item).filter(Boolean);

// The Receipts selections shown in the duplicate modal, so the user can see what the
// clone will carry before deciding whether to keep it.
export const getReceiptsKpiSelectionLabels = (lineItem: any): string[] => {
  const list = lineItem?.receiptProductList;
  return [...toLabels(list?.productList), ...toLabels(list?.productLowerCategories)];
};

// True when the source line item carries a Receipts Primary KPI. kpiObjectives is the
// parsed view of kpi_goal (see mapLineItemToForm); we fall back to the presence of
// selections so a line item with a malformed kpi_goal still surfaces the control.
export const hasReceiptsKpi = (lineItem: any): boolean => {
  if (Array.isArray(lineItem?.kpiObjectives)) {
    if (lineItem.kpiObjectives.some((kpi: any) => kpi?.name === RECEIPTS_KPI_NAME)) return true;
  }
  return getReceiptsKpiSelectionLabels(lineItem).length > 0;
};

// kpi_goal with the Receipts entry removed, ready to send as a paste-payload override.
// Returns null when there is no usable kpi_goal, which tells the caller there is nothing
// to override. `kpiGoal` is the parsed object from mapLineItemToForm
// ({ secondaryKpi: [...], coreKpi: [...] }).
export const buildKpiGoalWithoutReceipts = (kpiGoal: any): string | null => {
  if (!kpiGoal || typeof kpiGoal !== 'object') return null;
  const result = Object.entries(kpiGoal).reduce((accumulator, [kpiType, entries]) => {
    accumulator[kpiType] = (Array.isArray(entries) ? entries : []).filter(
      (entry: any) => !Object.keys(entry || {}).includes(RECEIPTS_KPI_NAME)
    );
    return accumulator;
  }, {} as Record<string, any[]>);
  return JSON.stringify(result);
};
